import "@akashnetwork/ui/styles";
import "nprogress/nprogress.css";
import "../styles/index.css";
import React from "react";
import { LoggerService } from "@akashnetwork/logging";
import { TooltipProvider } from "@akashnetwork/ui/components";
import { CustomSnackbarProvider, PopupProvider } from "@akashnetwork/ui/context";
import { cn } from "@akashnetwork/ui/utils";
import { AppCacheProvider } from "@mui/material-nextjs/v14-pagesRouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { GeistSans } from "geist/font/sans";
import { Provider as JotaiProvider } from "jotai";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import Router from "next/router";
import { NavigationGuardProvider } from "next-navigation-guard";
import type { NextSeoProps } from "next-seo/lib/types";
import NProgress from "nprogress";
import { ActivityHost } from "@src/components/activity/ActivityHost/ActivityHost";
import { DeploymentReadyHost } from "@src/components/activity/DeploymentReadyHost/DeploymentReadyHost";
import { AccountCreatedTracker } from "@src/components/analytics/AccountCreatedTracker/AccountCreatedTracker";
import { AppBootstrap } from "@src/components/AppBootstrap/AppBootstrap";
import { RequireAuth } from "@src/components/auth/RequireAuth/RequireAuth";
import { AddCreditsHost } from "@src/components/billing-usage/AddCreditsHost/AddCreditsHost";
import { CloseDeploymentConfirmHost } from "@src/components/deployments/CloseDeploymentConfirmHost/CloseDeploymentConfirmHost";
import { DeploymentCopyCleanup } from "@src/components/deployments/DeploymentCopyCleanup/DeploymentCopyCleanup";
import { RequireFairUsePolicy } from "@src/components/fair-use-policy/RequireFairUsePolicy/RequireFairUsePolicy";
import { AppThemeProvider } from "@src/components/layout/AppThemeProvider";
import { CustomIntlProvider } from "@src/components/layout/CustomIntlProvider";
import { PageHead } from "@src/components/layout/PageHead";
import { RequireOnboarding } from "@src/components/onboarding/RequireOnboarding/RequireOnboarding";
import { AccountDeletedNotice } from "@src/components/user/AccountDeletedNotice/AccountDeletedNotice";
import { UserProviders } from "@src/components/user/UserProviders/UserProviders";
import { WhatsNewHost } from "@src/components/whats-new/WhatsNewHost/WhatsNewHost";
import { BlockchainStatusProvider } from "@src/context/BlockchainStatusProvider";
import { BootLoadingProvider } from "@src/context/BootLoadingProvider/BootLoadingProvider";
import { ColorModeProvider } from "@src/context/CustomThemeContext";
import { FlagProvider, WaitForFeatureFlags } from "@src/context/FlagProvider/FlagProvider";
import { OrganizationProvider } from "@src/context/OrganizationProvider/OrganizationProvider";
import { PaymentPollingProvider } from "@src/context/PaymentPollingProvider";
import { ServicesProvider } from "@src/context/ServicesProvider";
import { RootContainerProvider, useRootContainer } from "@src/context/ServicesProvider/RootContainerProvider";
import { WalletProvider } from "@src/context/WalletProvider";
import type { PageWithAuth } from "@src/lib/pages/definePublicPage";
import { store } from "@src/store/global-store";
import { tolerateTranslatorDomEdits } from "@src/utils/tolerateTranslatorDomEdits/tolerateTranslatorDomEdits";
interface Props extends AppProps {
  seo?: NextSeoProps;
}
NProgress.configure({
  minimum: 0.2
});

//Binding events.
Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());
if (typeof window !== "undefined") {
  tolerateTranslatorDomEdits(Node.prototype, LoggerService.forContext("TranslatorDomGuard"));
}
const App: React.FunctionComponent<Props> = props => {
  const {
    Component,
    pageProps
  } = props;
  const isPublic = (Component as PageWithAuth).auth === "public";
  return <AppRoot {...props} data-sentry-element="AppRoot" data-sentry-component="App" data-sentry-source-file="_app.tsx">
      <BootLoadingProvider data-sentry-element="BootLoadingProvider" data-sentry-source-file="_app.tsx">
        <UserProviders data-sentry-element="UserProviders" data-sentry-source-file="_app.tsx">
          <AccountCreatedTracker data-sentry-element="AccountCreatedTracker" data-sentry-source-file="_app.tsx" />
          <AccountDeletedNotice data-sentry-element="AccountDeletedNotice" data-sentry-source-file="_app.tsx" />
          <RequireAuth isPublic={isPublic} data-sentry-element="RequireAuth" data-sentry-source-file="_app.tsx">
            <FlagProvider data-sentry-element="FlagProvider" data-sentry-source-file="_app.tsx">
              <OrganizationProvider data-sentry-element="OrganizationProvider" data-sentry-source-file="_app.tsx">
                <WalletProvider data-sentry-element="WalletProvider" data-sentry-source-file="_app.tsx">
                  <PaymentPollingProvider data-sentry-element="PaymentPollingProvider" data-sentry-source-file="_app.tsx">
                    <AddCreditsHost data-sentry-element="AddCreditsHost" data-sentry-source-file="_app.tsx" />
                    <DeploymentCopyCleanup data-sentry-element="DeploymentCopyCleanup" data-sentry-source-file="_app.tsx" />
                    <CloseDeploymentConfirmHost data-sentry-element="CloseDeploymentConfirmHost" data-sentry-source-file="_app.tsx" />
                    <ActivityHost data-sentry-element="ActivityHost" data-sentry-source-file="_app.tsx" />
                    <DeploymentReadyHost data-sentry-element="DeploymentReadyHost" data-sentry-source-file="_app.tsx" />
                    <WhatsNewHost data-sentry-element="WhatsNewHost" data-sentry-source-file="_app.tsx" />
                    <NavigationGuardProvider data-sentry-element="NavigationGuardProvider" data-sentry-source-file="_app.tsx">
                      <RequireOnboarding isPublic={isPublic} data-sentry-element="RequireOnboarding" data-sentry-source-file="_app.tsx">
                        <WaitForFeatureFlags data-sentry-element="WaitForFeatureFlags" data-sentry-source-file="_app.tsx">
                          <RequireFairUsePolicy isPublic={isPublic} data-sentry-element="RequireFairUsePolicy" data-sentry-source-file="_app.tsx">
                            <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                          </RequireFairUsePolicy>
                        </WaitForFeatureFlags>
                      </RequireOnboarding>
                    </NavigationGuardProvider>
                  </PaymentPollingProvider>
                </WalletProvider>
              </OrganizationProvider>
            </FlagProvider>
          </RequireAuth>
        </UserProviders>
      </BootLoadingProvider>
    </AppRoot>;
};
export default App;
const LocalNoteManager = dynamic(() => import("@src/components/LocalNoteManager/LocalNoteManager").then(mod => mod.LocalNoteManager), {
  ssr: false
});
function AppRoot(props: Props & {
  children: React.ReactNode;
}) {
  const {
    queryClient
  } = useRootContainer();
  return <main className={cn("h-full bg-background font-sans antialiased", GeistSans.variable)} data-sentry-component="AppRoot" data-sentry-source-file="_app.tsx">
      <PageHead pageSeo={props.pageProps.seo} data-sentry-element="PageHead" data-sentry-source-file="_app.tsx" />

      <RootContainerProvider data-sentry-element="RootContainerProvider" data-sentry-source-file="_app.tsx">
        <AppCacheProvider {...props} data-sentry-element="AppCacheProvider" data-sentry-source-file="_app.tsx">
          <CustomIntlProvider data-sentry-element="CustomIntlProvider" data-sentry-source-file="_app.tsx">
            <JotaiProvider store={store} data-sentry-element="JotaiProvider" data-sentry-source-file="_app.tsx">
              <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
                <AppThemeProvider data-sentry-element="AppThemeProvider" data-sentry-source-file="_app.tsx">
                  <ColorModeProvider data-sentry-element="ColorModeProvider" data-sentry-source-file="_app.tsx">
                    <CustomSnackbarProvider data-sentry-element="CustomSnackbarProvider" data-sentry-source-file="_app.tsx">
                      <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="_app.tsx">
                        <PopupProvider data-sentry-element="PopupProvider" data-sentry-source-file="_app.tsx">
                          <BlockchainStatusProvider data-sentry-element="BlockchainStatusProvider" data-sentry-source-file="_app.tsx">
                            <ServicesProvider data-sentry-element="ServicesProvider" data-sentry-source-file="_app.tsx">
                              <AppBootstrap data-sentry-element="AppBootstrap" data-sentry-source-file="_app.tsx" />
                              <LocalNoteManager data-sentry-element="LocalNoteManager" data-sentry-source-file="_app.tsx" />
                              {props.children}
                            </ServicesProvider>
                          </BlockchainStatusProvider>
                        </PopupProvider>
                      </TooltipProvider>
                    </CustomSnackbarProvider>
                  </ColorModeProvider>
                </AppThemeProvider>
              </QueryClientProvider>
            </JotaiProvider>
          </CustomIntlProvider>
        </AppCacheProvider>
      </RootContainerProvider>
    </main>;
}